import dayjs from "dayjs";

import { lookbackDateBounds } from "../../services/pricingLookback";

/**
 * @param {import("dayjs").Dayjs | string | Date | null | undefined} dateFrom
 * @param {import("dayjs").Dayjs | string | Date | null | undefined} dateTo
 * @param {{ style?: "range" | "sentence" }} [options]
 */
export function formatPricingReportDatePeriod(dateFrom, dateTo, { style = "range" } = {}) {
  const from = dayjs(dateFrom);
  const to = dayjs(dateTo);
  if (!from.isValid() || !to.isValid()) {
    return "";
  }

  const fromLabel = from.format("MMM D, YYYY");
  const toLabel = to.format("MMM D, YYYY");

  if (style === "sentence") {
    if (from.isSame(to, "day")) {
      return `on ${fromLabel}`;
    }
    return `from ${fromLabel} to ${toLabel}`;
  }

  if (from.isSame(to, "day")) {
    return fromLabel;
  }
  return `${fromLabel} – ${toLabel}`;
}

export function formatQuarterlyLookbackDatePeriod(lookbackYears = 1) {
  const { dateFrom, dateTo } = lookbackDateBounds(lookbackYears);
  return formatPricingReportDatePeriod(dateFrom, dateTo, { style: "sentence" });
}

function chartDateContext({ dateFrom, dateTo } = {}) {
  return { dateFrom, dateTo };
}

export function monthlyByProductChartTitle(granularity = "month") {
  return granularity === "week"
    ? "Weekly average over time by product"
    : "Monthly average over time by product";
}

export function monthlyByProductChartSubtitle(
  hasSelectedProduct,
  granularity = "month",
  dateContext = {},
  combinedView = false,
  groupedProductCount = 0
) {
  const { dateFrom, dateTo } = chartDateContext(dateContext);
  const period = formatPricingReportDatePeriod(dateFrom, dateTo, { style: "sentence" });
  const bucket = granularity === "week" ? "week" : "month";
  const buckets = granularity === "week" ? "weeks" : "months";

  if (combinedView) {
    return period
      ? `All products on one chart ${period}. Prices are smoothed over three months. The top products are highlighted; dashed lines show ${buckets} with no prices. Click a name in the legend to focus on one product.`
      : `All products on one chart. Prices are smoothed over three months. The top products are highlighted; dashed lines show ${buckets} with no prices. Click a name in the legend to focus on one product.`;
  }

  if (hasSelectedProduct) {
    const groupedNote =
      groupedProductCount > 1
        ? " Selected products are grouped as one series."
        : "";
    return period
      ? `Shows how the average price changed each ${bucket} ${period}. The line is the typical price; the bars show the lowest and highest prices in that ${bucket}.${groupedNote}`
      : `Shows how the average price changed each ${bucket}. The line is the typical price; the bars show the lowest and highest prices in that ${bucket}.${groupedNote}`;
  }

  return period
    ? `Average price each ${bucket} for each product ${period}. Each product has its own chart with the same time scale. When no product is selected, the top 10 products are shown. Click a chart to filter the report to that product.`
    : `Average price each ${bucket} for each product. Each product has its own chart with the same time scale. When no product is selected, the top 10 products are shown. Click a chart to filter the report to that product.`;
}

export function monthlyByRetailerChartTitle() {
  return "Monthly average over time by retailer";
}

export function monthlyByRetailerChartSubtitle(combinedView = false, dateContext = {}) {
  const { dateFrom, dateTo } = chartDateContext(dateContext);
  const period = formatPricingReportDatePeriod(dateFrom, dateTo, { style: "sentence" });

  if (combinedView) {
    return period
      ? `All retailers on one chart ${period}. Prices are smoothed over three months. The top retailers are highlighted; dashed lines show months with no prices. Click a name in the legend to focus on one retailer.`
      : "All retailers on one chart. Prices are smoothed over three months. The top retailers are highlighted; dashed lines show months with no prices. Click a name in the legend to focus on one retailer.";
  }

  return period
    ? `Average price each month at each retailer ${period}. Each retailer has its own chart with the same time scale.`
    : "Average price each month at each retailer. Each retailer has its own chart with the same time scale.";
}

export function monthlyByProvinceChartTitle() {
  return "Monthly average over time by province";
}

export function monthlyByProvinceChartSubtitle(combinedView = false, dateContext = {}) {
  const { dateFrom, dateTo } = chartDateContext(dateContext);
  const period = formatPricingReportDatePeriod(dateFrom, dateTo, { style: "sentence" });

  if (combinedView) {
    return period
      ? `All provinces on one chart ${period}. Prices are smoothed over three months. The top provinces are highlighted; dashed lines show months with no prices. Click a name in the legend to focus on one province.`
      : "All provinces on one chart. Prices are smoothed over three months. The top provinces are highlighted; dashed lines show months with no prices. Click a name in the legend to focus on one province.";
  }

  return period
    ? `Average price each month in each province ${period}. Each province has its own chart with the same time scale.`
    : "Average price each month in each province. Each province has its own chart with the same time scale.";
}

export function quarterlyChartTitle() {
  return "Quarterly average";
}

export function quarterlyChartSubtitle(lookbackYears = 1) {
  const period = formatQuarterlyLookbackDatePeriod(lookbackYears);
  if (period) {
    return `Average price by quarter ${period}.`;
  }
  return "Average price by quarter.";
}

export function snapshotRetailerChartTitle(productName) {
  return `Average unit price by retailer for ${productName}`;
}

export function snapshotRetailerChartDescription(productName, dateContext = {}) {
  const { dateFrom, dateTo } = chartDateContext(dateContext);
  const period = formatPricingReportDatePeriod(dateFrom, dateTo, { style: "sentence" });
  if (period) {
    return `Average price for ${productName} at each retailer ${period}.`;
  }
  return `Average price for ${productName} at each retailer.`;
}

export function snapshotProvinceChartTitle(productName) {
  return `Average unit price by province for ${productName}`;
}

export function snapshotProvinceChartDescription(productName, dateContext = {}) {
  const { dateFrom, dateTo } = chartDateContext(dateContext);
  const period = formatPricingReportDatePeriod(dateFrom, dateTo, { style: "sentence" });
  if (period) {
    return `Average price for ${productName} in each province ${period}.`;
  }
  return `Average price for ${productName} in each province.`;
}
